(() => {
  const STORAGE_KEY = 'mission_referral_v1';
  const DISMISSED_KEY = 'mission_referral_banner_dismissed';
  const DISCOUNT_SESSION_KEY = 'mission_referral_discount_token';
  const DISCOUNT_CODE = 'MISSION10';
  const ATTRIBUTION_MS = 30 * 24 * 60 * 60 * 1000;
  const TOKEN_PATTERN = /^M(\d{5,})$/;
  const CART_ATTRIBUTES = {
    '__mission_referrer_id': null,
    '__mission_referral_offer': null,
  };

  const referralRoot = document.querySelector('[data-mission-referrals-root]');
  const currentCustomerId = referralRoot?.dataset.currentCustomerId || '';

  const parseToken = (value) => {
    const token = String(value || '').trim().toUpperCase();
    const match = token.match(TOKEN_PATTERN);
    return match ? { token, customerId: match[1] } : null;
  };

  const getStoredReferral = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
      const parsed = parseToken(stored?.token);
      if (!parsed || !stored?.expiresAt || Date.now() >= stored.expiresAt) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return { ...parsed, expiresAt: stored.expiresAt };
    } catch (_) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  };

  const setStoredReferral = (parsed) => {
    const referral = { token: parsed.token, expiresAt: Date.now() + ATTRIBUTION_MS };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(referral));
    return { ...parsed, expiresAt: referral.expiresAt };
  };

  const updateCartAttributes = async (attributes) => {
    try {
      const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error('Unable to update cart');
      return true;
    } catch (_) {
      // Keep the first-party attribution locally and retry on the next page load.
      return false;
    }
  };

  const clearReferral = () => {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem('mission_referral_cart_sync');
    updateCartAttributes(CART_ATTRIBUTES);
  };

  const syncReferralToCart = async (referral) => {
    const syncKey = `${referral.token}:${referral.expiresAt}`;
    if (sessionStorage.getItem('mission_referral_cart_sync') === syncKey) return;

    try {
      const response = await fetch(window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js', {
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('Unable to read cart');
      const synced = await updateCartAttributes({
        '__mission_referrer_id': referral.customerId,
        '__mission_referral_offer': 'GIVE $10. GET $15.',
      });
      if (synced) sessionStorage.setItem('mission_referral_cart_sync', syncKey);
    } catch (_) {
      // A later page load will retry without interrupting shopping.
    }
  };

  const applyReferralDiscount = (referral, hasIncomingReferral) => {
    if (!hasIncomingReferral || sessionStorage.getItem(DISCOUNT_SESSION_KEY) === referral.token) return false;

    sessionStorage.setItem(DISCOUNT_SESSION_KEY, referral.token);
    const destination = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(`/discount/${DISCOUNT_CODE}?redirect=${encodeURIComponent(destination)}`);
    return true;
  };

  const showOfferBanner = () => {
    const banner = document.querySelector('[data-mission-referral-banner]');
    if (!banner || sessionStorage.getItem(DISMISSED_KEY) === 'true') return;
    banner.hidden = false;
    banner.querySelector('[data-mission-referral-dismiss]')?.addEventListener('click', () => {
      banner.hidden = true;
      sessionStorage.setItem(DISMISSED_KEY, 'true');
    });
  };

  const initializeReferral = () => {
    const incoming = parseToken(new URLSearchParams(window.location.search).get('ref'));
    let referral = getStoredReferral();

    if (incoming && incoming.customerId === currentCustomerId) {
      clearReferral();
      return;
    }

    if (!referral && incoming) referral = setStoredReferral(incoming);

    if (referral && referral.customerId === currentCustomerId) {
      clearReferral();
      return;
    }

    if (referral) {
      if (applyReferralDiscount(referral, Boolean(incoming))) return;
      syncReferralToCart(referral);
      showOfferBanner();
    }
  };

  const initializeShareControls = () => {
    document.querySelectorAll('[data-mission-referral-share]').forEach((container) => {
      const url = container.dataset.missionReferralShare;
      const copyButton = container.querySelector('[data-mission-copy-link]');
      const shareButton = container.querySelector('[data-mission-native-share]');
      const status = container.querySelector('[data-mission-share-status]');
      let statusTimer;

      const showStatus = (message) => {
        if (!status) return;
        status.textContent = message;
        clearTimeout(statusTimer);
        statusTimer = setTimeout(() => { status.textContent = ''; }, 2400);
      };

      copyButton?.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(url);
          showStatus('LINK COPIED');
        } catch (_) {
          const input = container.querySelector('[data-mission-referral-url]');
          input?.select();
          const copied = document.execCommand('copy');
          showStatus(copied ? 'LINK COPIED' : 'COPY THE LINK ABOVE');
        }
      });

      if (shareButton) {
        if (!navigator.share) {
          shareButton.hidden = true;
        } else {
          shareButton.addEventListener('click', async () => {
            try {
              await navigator.share({
                title: 'GIVE $10. GET $15.',
                text: 'Get $10 off your first $75+ BuiltByMission order.',
                url,
              });
            } catch (error) {
              if (error?.name !== 'AbortError') showStatus('COPY THE LINK ABOVE');
            }
          });
        }
      }
    });
  };

  initializeReferral();
  initializeShareControls();
})();
